import { formatDate } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { phoneValidator } from '../utils/validators/phone.validator';
import { mapAddressValidator } from '../utils/validators/map-address.validator';

import type { AppMode } from '../models/app-mode.model';
import {
  HospitalityCatalogAdditionalInfo,
  HospitalityCatalogApplianceGuide,
  HospitalityCatalogCheckInEntry,
  HospitalityCatalogCheckOutEntry,
  HospitalityCatalogEntries,
  HospitalityCatalogHouseRule,
  HospitalityCatalogLink,
  HospitalityLinkDocument,
  HospitalityFeedback,
  HospitalityGuide,
  HospitalityLandingLink,
  HospitalityLandingSettings,
  HospitalityPreviewLink,
  HospitalityPreviewModel,
  HospitalityPreviewSection,
  HospitalityTimelineItem,
} from './company.models';
import {
  HOSPITALITY_DIGITAL_KEY_ACCESS_TYPES,
  HospitalityDigitalKey,
  HospitalityDigitalKeyAccessType,
} from '../models/hospitality-digital-key.model';

interface TimeRangeSlot {
  start: Date | null;
  end: Date | null;
}

interface HospitalityEntryView {
  control: FormGroup;
  index: number;
}

interface HospitalityEntryTrackable extends HospitalityEntryView {
  trackId: string | number;
}

export abstract class CompanyHospitalityMixin {
  hospitalityPreview: HospitalityPreviewModel = { infoSections: [] };
  showAllRules = false;
  showAllChecklist = false;
  hospitalitySectionExpanded: Partial<Record<keyof HospitalityCatalogEntries, boolean>> = {
    houseRules: false,
    checkIns: false,
    checkOuts: false,
    applianceGuides: false,
    additionalInfos: false,
  };

  readonly digitalKeyAccessTypes = HOSPITALITY_DIGITAL_KEY_ACCESS_TYPES;

  protected readonly HOSPITALITY_LANDING_LIMITS = {
    welcomeMessage: 80,
    guestNotes: 80,
    hostName: 120,
    hostPhone: 30,
    hostEmail: 254,
    hostWhatsapp: 120,
    feedbackInstructions: 500,
    feedbackEmail: 254,
    feedbackPhone: 30,
    feedbackFormUrl: 2048,
  } as const;

  protected readonly HOSPITALITY_LIMITS = {
    title: 120,
    summary: 500,
    details: 80,
    rule: 120,
    instructions: 500,
    appliance: 120,
    linkLabel: 120,
    linkUrl: 500,
    digitalKeyCode: 80,
    digitalKeyLink: 500,
    digitalKeyNotes: 500,
    location: 240,
    contact: 240,
    checklistTitle: 120,
    checklistItem: 80,
  };

  private readonly HOSPITALITY_SECTION_KEYS: Array<keyof HospitalityCatalogEntries> = [
    'houseRules',
    'checkIns',
    'checkOuts',
    'applianceGuides',
    'additionalInfos',
  ];

  protected abstract fb: FormBuilder;
  protected abstract translate: TranslateService;
  protected abstract companyForm: FormGroup;
  protected abstract appMode: AppMode;

  protected abstract markFormDirty(): void;

  toggleShowAllRules(): void {
    this.showAllRules = !this.showAllRules;
  }

  toggleShowAllChecklist(): void {
    this.showAllChecklist = !this.showAllChecklist;
  }

  protected ensureHospitalityLandingGroup(settings?: HospitalityLandingSettings | null): void {
    if (!this.companyForm.contains('hospitalityLanding')) {
      this.companyForm.addControl('hospitalityLanding', this.createHospitalityLandingGroup(settings || undefined));
    }

    const group = this.companyForm.get('hospitalityLanding') as FormGroup | null;
    if (!group) {
      return;
    }

    if (settings) {
      this.setHospitalityLanding(settings);
    }
  }

  protected ensureHospitalityEntriesGroup(entries?: HospitalityCatalogEntries | null): void {
    if (!this.companyForm.contains('hospitalityEntries')) {
      this.companyForm.addControl('hospitalityEntries', this.createHospitalityEntriesGroup(entries || undefined));
      this.companyForm.get('hospitalityEntries')?.valueChanges.subscribe(() => this.computeHospitalityPreview());
    }
    if (entries) {
      this.setHospitalityEntries(entries);
    }
  }

  formatHospitalityPreviewTime(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? ''
        : formatDate(value, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    const cleaned = this.cleanHospitalityString(value as string | Date | null);
    if (!cleaned) {
      return '';
    }

    const parsedTime = this.parseTimeStringToDate(cleaned);
    if (parsedTime) {
      return formatDate(parsedTime, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    const parsedDate = new Date(cleaned);
    if (!Number.isNaN(parsedDate.getTime())) {
      return formatDate(parsedDate, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    return cleaned;
  }

  hospitalityCatalogLinks(sectionKey: keyof HospitalityCatalogEntries, index: number): FormArray | null {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return null;
    }
    return (array.at(index)?.get('links') as FormArray) || null;
  }

  hospitalityCatalogDigitalKeys(index: number): FormArray | null {
    const array = this.getHospitalityCatalogArray('checkIns');
    if (!array) {
      return null;
    }
    return (array.at(index)?.get('digitalKeys') as FormArray) || null;
  }

  hospitalityCatalogChecklist(index: number): FormGroup | null {
    const array = this.getHospitalityCatalogArray('checkOuts');
    if (!array) {
      return null;
    }
    return (array.at(index)?.get('checklist') as FormGroup) || null;
  }

  hospitalityCatalogChecklistItems(index: number): FormArray | null {
    const checklist = this.hospitalityCatalogChecklist(index);
    if (!checklist) {
      return null;
    }
    return (checklist.get('items') as FormArray) || null;
  }

  hasHospitalitySectionDuplicate(sectionKey: keyof HospitalityCatalogEntries): boolean {
    const group = this.hospitalityEntriesGroup;
    if (!group?.errors) {
      return false;
    }
    const key = `duplicate_${String(sectionKey)}`;
    return !!group.errors[key];
  }

  getHospitalityDuplicateField(sectionKey: keyof HospitalityCatalogEntries): string | undefined {
    const group = this.hospitalityEntriesGroup;
    if (!group?.errors) {
      return undefined;
    }
    const key = `duplicate_${String(sectionKey)}`;
    const info = group.errors[key];
    return typeof info === 'object' ? info.field : undefined;
  }

  getDeletedHospitalityControls(sectionKey: keyof HospitalityCatalogEntries): HospitalityEntryTrackable[] {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return [];
    }
    return array.controls
      .map((control, index) => {
        const group = control as FormGroup;
        const rawId = group.get('_id')?.value;
        return {
          control: group,
          index,
          trackId: rawId ?? `deleted-${index}`,
        };
      })
      .filter((entry) => entry.control.get('isDeleted')?.value);
  }

  hospitalityEntryTrackBy(_index: number, entry: HospitalityEntryTrackable): string | number {
    return entry.trackId;
  }

  addHospitalityCatalogLink(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    const links = this.hospitalityCatalogLinks(sectionKey, index);
    if (!links) {
      return;
    }
    links.push(this.createHospitalityEntryLinkGroup());
    this.markFormDirty();
  }

  addHospitalityCatalogDigitalKey(index: number): void {
    const digitalKeys = this.hospitalityCatalogDigitalKeys(index);
    if (!digitalKeys) {
      return;
    }
    digitalKeys.push(this.createHospitalityDigitalKeyGroup());
    this.markFormDirty();
  }

  addHospitalityCatalogChecklistItem(index: number): void {
    const items = this.hospitalityCatalogChecklistItems(index);
    if (!items) {
      return;
    }
    items.push(this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistItem)]));
    this.markFormDirty();
  }

  removeHospitalityCatalogLink(sectionKey: keyof HospitalityCatalogEntries, entryIndex: number, linkIndex: number): void {
    const links = this.hospitalityCatalogLinks(sectionKey, entryIndex);
    if (!links) {
      return;
    }
    links.removeAt(linkIndex);
    this.markFormDirty();
  }

  removeHospitalityCatalogDigitalKey(entryIndex: number, keyIndex: number): void {
    const digitalKeys = this.hospitalityCatalogDigitalKeys(entryIndex);
    if (!digitalKeys) {
      return;
    }
    digitalKeys.removeAt(keyIndex);
    this.markFormDirty();
  }

  removeHospitalityCatalogChecklistItem(entryIndex: number, itemIndex: number): void {
    const items = this.hospitalityCatalogChecklistItems(entryIndex);
    if (!items) {
      return;
    }
    items.removeAt(itemIndex);
    this.markFormDirty();
  }

  addHospitalityCatalogEntry(sectionKey: keyof HospitalityCatalogEntries): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return;
    }

    switch (sectionKey) {
      case 'houseRules':
        array.push(this.createHouseRuleEntryGroup());
        break;
      case 'checkIns':
        array.push(this.createCheckInEntryGroup());
        break;
      case 'checkOuts':
        array.push(this.createCheckOutEntryGroup());
        break;
      case 'applianceGuides':
        array.push(this.createApplianceGuideEntryGroup());
        break;
      case 'additionalInfos':
        array.push(this.createAdditionalInfoEntryGroup());
        break;
    }

    this.openHospitalityEntryEditing(sectionKey, array.length - 1);
    this.markFormDirty();
  }

  removeHospitalityCatalogEntry(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return;
    }
    const control = array.at(index) as FormGroup | undefined;
    if (!control) {
      return;
    }
    const hasId = !!control.get('_id')?.value;
    this.closeHospitalityEntryEditing(sectionKey, index);
    if (hasId) {
      control.get('isDeleted')?.setValue(true, { emitEvent: false });
      control.get('deletedAt')?.setValue(new Date().toISOString(), { emitEvent: false });
    } else {
      array.removeAt(index);
    }
    this.markFormDirty();
  }

  restoreHospitalityCatalogEntry(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return;
    }
    const control = array.at(index) as FormGroup | undefined;
    if (!control) {
      return;
    }
    control.get('isDeleted')?.setValue(false, { emitEvent: false });
    control.get('deletedAt')?.setValue(null, { emitEvent: false });
  }

  applianceGuideSteps(index: number): FormArray | null {
    const array = this.getHospitalityCatalogArray('applianceGuides');
    if (!array) {
      return null;
    }
    return (array.at(index)?.get('steps') as FormArray) || null;
  }

  addApplianceGuideStep(index: number): void {
    const steps = this.applianceGuideSteps(index);
    if (!steps) {
      return;
    }
    steps.push(this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.details)]));
    this.markFormDirty();
  }

  removeApplianceGuideStep(entryIndex: number, stepIndex: number): void {
    const steps = this.applianceGuideSteps(entryIndex);
    if (!steps) {
      return;
    }
    steps.removeAt(stepIndex);
    this.markFormDirty();
  }

  toggleHospitalityEntryEditing(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    if (this.isHospitalityEntryEditing(sectionKey, index)) {
      this.closeHospitalityEntryEditing(sectionKey, index);
    } else {
      this.openHospitalityEntryEditing(sectionKey, index);
    }
  }

  openHospitalityEntryEditing(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return;
    }
    this.hospitalitySectionExpanded[sectionKey] = true;
    const control = array.at(index) as FormGroup | undefined;
    if (!control || control.get('isDeleted')?.value) {
      return;
    }
    array.controls.forEach((item, itemIndex) => {
      const group = item as FormGroup | undefined;
      group?.get('isEditing')?.setValue(itemIndex === index, { emitEvent: false });
    });
    this.collapseOtherHospitalitySections(sectionKey);
  }

  closeHospitalityEntryEditing(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return;
    }
    const control = array.at(index) as FormGroup | undefined;
    control?.get('isEditing')?.setValue(false, { emitEvent: false });
  }

  isHospitalitySectionExpanded(sectionKey: keyof HospitalityCatalogEntries): boolean {
    return !!this.hospitalitySectionExpanded[sectionKey];
  }

  setHospitalitySectionExpanded(sectionKey: keyof HospitalityCatalogEntries, expanded: boolean): void {
    this.hospitalitySectionExpanded[sectionKey] = expanded;
    if (expanded) {
      this.collapseOtherHospitalitySections(sectionKey);
    }
  }

  onHospitalitySectionActiveChange(sectionKey: keyof HospitalityCatalogEntries, expanded: boolean): void {
    this.hospitalitySectionExpanded[sectionKey] = expanded;
    if (expanded) {
      this.HOSPITALITY_SECTION_KEYS
        .filter((key) => key !== sectionKey)
        .forEach((key) => {
          this.hospitalitySectionExpanded[key] = false;
        });
    }
    if (!expanded) {
      const array = this.getHospitalityCatalogArray(sectionKey);
      array?.controls.forEach((_, idx) => this.closeHospitalityEntryEditing(sectionKey, idx));
    }
  }

  isHospitalityEntryEditing(sectionKey: keyof HospitalityCatalogEntries, index: number): boolean {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return false;
    }
    const control = array.at(index) as FormGroup | undefined;
    if (!control) {
      return false;
    }
    return !!control.get('isEditing')?.value;
  }

  protected computeHospitalityPreview(): void {
    if (!this.companyForm || this.appMode !== 'hospitality') {
      this.hospitalityPreview = { infoSections: [] };
      return;
    }

    const landingGroup = this.hospitalityLandingGroup;
    const entriesGroup = this.hospitalityEntriesGroup;
    if (!landingGroup || !entriesGroup) {
      this.hospitalityPreview = { infoSections: [] };
      return;
    }

    const landing = landingGroup.getRawValue();
    const entries = (entriesGroup.getRawValue() as HospitalityCatalogEntries) || {};

    const clean = (value?: string | null) => this.cleanHospitalityString(value);
    const filterActive = <T extends { isDeleted?: boolean }>(list?: T[]): T[] =>
      Array.isArray(list) ? list.filter((item) => !item?.isDeleted) : [];

    const welcomeMessage = clean(landing?.welcomeMessage);
    const guestNotes = clean(landing?.guestNotes);

    const supportHours = clean(landing?.hostContact?.supportHours);
    const hostName = clean(landing?.hostContact?.name);
    const hostPhone = clean(landing?.hostContact?.phone);
    const hostEmail = clean(landing?.hostContact?.email);
    const hostWhatsapp = clean(landing?.hostContact?.whatsapp);
    const hostHasDetails = hostName || hostPhone || hostEmail || hostWhatsapp || supportHours;
    const hostContact = hostHasDetails
      ? { name: hostName, phone: hostPhone, email: hostEmail, whatsapp: hostWhatsapp, supportHours }
      : undefined;

    const sanitizeLinks = (links?: HospitalityCatalogLink[]): HospitalityPreviewLink[] =>
      Array.isArray(links)
        ? links
            .map((link) => this.cleanHospitalityLink(link))
            .filter((link): link is HospitalityCatalogLink => !!link)
        : [];

    const sanitizeDigitalKeys = (
      keys?: Array<HospitalityDigitalKey | HospitalityCatalogLink>,
    ): HospitalityDigitalKey[] =>
      Array.isArray(keys)
        ? keys
            .map((key) => this.cleanHospitalityDigitalKey(key))
            .filter((key): key is HospitalityDigitalKey => !!key)
        : [];

    const timeline: HospitalityTimelineItem[] = [];
    const mapCheckInEntry = (entry: HospitalityCatalogCheckInEntry): HospitalityTimelineItem | null => {
      const scheduleFrom = clean(entry?.schedule?.from);
      const scheduleUntil = clean(entry?.schedule?.until);
      const location = clean(entry?.location);
      const contact = clean(entry?.contact);
      const legacySummary = clean((entry as any)?.summary);
      const legacyDetails = clean((entry as any)?.details);
      const instructions = clean(entry?.instructions) || legacySummary || legacyDetails;
      const links = sanitizeLinks(entry?.links);
      const digitalKeys = sanitizeDigitalKeys(entry?.digitalKeys);

      const hasContent =
        !!(instructions || links.length || digitalKeys.length || scheduleFrom || scheduleUntil || location || contact);
      if (!hasContent) {
        return null;
      }

      return {
        type: 'check-in',
        titleKey: 'LANDING.HOSPITALITY.CHECK_IN',
        from: scheduleFrom,
        until: scheduleUntil,
        location,
        instructions,
        contact,
        links: links.length ? links : undefined,
        digitalKeys: digitalKeys.length ? digitalKeys : undefined,
      };
    };

    const mapCheckOutEntry = (entry: HospitalityCatalogCheckOutEntry): HospitalityTimelineItem | null => {
      const checkOutTime = clean(entry?.checkOutTime ?? (entry as any)?.deadline);
      const location = clean(entry?.dropOffLocation ?? (entry as any)?.location);
      const contact = clean(entry?.contact);
      const explicitInstructions = clean(entry?.instructions);
      const legacySummary = clean((entry as any)?.summary);
      const legacyDetails = clean((entry as any)?.details);
      const detailText = legacyDetails || '';
      const checklistGroup = entry?.checklist;
      const checklistTitle = clean(checklistGroup?.title);
      const rawChecklistItems: Array<string | null | undefined> = Array.isArray(checklistGroup?.items)
        ? (checklistGroup?.items as Array<string | null | undefined>)
        : [];
      const explicitChecklistItems = rawChecklistItems
        .map((item) => this.cleanHospitalityString(item) || '')
        .map((item) => item.trim())
        .filter(Boolean);
      const fallbackSource = detailText || explicitInstructions || legacySummary || '';
      const fallbackChecklistItems = !explicitChecklistItems.length
        ? fallbackSource
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
        : [];
      const checklist = checklistTitle || explicitChecklistItems.length
        ? {
            title: checklistTitle || undefined,
            items: explicitChecklistItems.length ? explicitChecklistItems : undefined,
          }
        : fallbackChecklistItems.length
          ? { items: fallbackChecklistItems }
          : undefined;
      const instructions = explicitInstructions || legacySummary || legacyDetails || undefined;
      const links = sanitizeLinks(entry?.links);

      const hasContent = !!(instructions || checklist || links.length || checkOutTime || location || contact);
      if (!hasContent) {
        return null;
      }

      return {
        type: 'check-out',
        titleKey: 'LANDING.HOSPITALITY.CHECK_OUT',
        from: undefined,
        until: checkOutTime,
        location,
        instructions,
        contact,
        links: links.length ? links : undefined,
        checklist,
      };
    };

    filterActive(entries.checkIns).forEach((entry) => {
      const item = mapCheckInEntry(entry);
      if (item) {
        timeline.push(item);
      }
    });
    filterActive(entries.checkOuts).forEach((entry) => {
      const item = mapCheckOutEntry(entry);
      if (item) {
        timeline.push(item);
      }
    });

    const houseRuleEntries = filterActive(entries.houseRules);
    let houseRules;
    if (houseRuleEntries.length) {
      const rules = houseRuleEntries
        .flatMap((entry) => (clean(entry.rule) || '').split('\n'))
        .map((line) => line.trim())
        .filter(Boolean);
      if (rules.length) {
        houseRules = {
          rules,
        };
      }
    }

    const guides = filterActive(entries.applianceGuides)
      .map((entry) => {
        const appliance = this.cleanHospitalityString((entry as any).appliance);
        const summary = clean(entry.summary);
        const steps = this.normalizeApplianceGuideSteps(entry.steps, (entry as any)?.details);
        const resources = sanitizeLinks(entry.links);
        if (!appliance && !summary && !steps.length && !resources.length) {
          return undefined;
        }
        const resolvedSummary = summary || (steps.length ? steps[0] : '');
        return {
          appliance,
          summary: resolvedSummary,
          steps: steps.length ? steps : undefined,
          resources: resources.length ? resources : undefined,
        } as HospitalityGuide;
      })
      .filter((guide): guide is HospitalityGuide => !!guide);

    const infoSections: HospitalityPreviewSection[] = filterActive(entries.additionalInfos)
      .map((entry) => {
        const title = clean(entry.title);
        const body = clean(entry.body) || clean((entry as any)?.details);
        const links = sanitizeLinks(entry.links);
        return { title, body, links } as HospitalityPreviewSection;
      })
      .filter((s) => !!(s.title || s.body || (s.links?.length)));

    let feedback: HospitalityFeedback | undefined;
    if (landing?.feedback) {
      const fb: HospitalityFeedback = {
        instructions: clean(landing.feedback.instructions),
        email: clean(landing.feedback.email),
        phone: clean(landing.feedback.phone),
        formUrl: clean(landing.feedback.formUrl),
      };
      const hasAny =
        !!(fb.instructions || fb.email || fb.phone || fb.formUrl);
      feedback = hasAny ? fb : undefined;
    }

    this.hospitalityPreview = {
      welcomeMessage,
      guestNotes,
      hostContact,
      timeline: timeline.length ? timeline : undefined,
      houseRules,
      guides: guides.length ? guides : undefined,
      localTips: undefined,
      feedback,
      infoSections,
    };
  }

  protected extractHospitalityLanding(): HospitalityLandingSettings | null {
    const group = this.hospitalityLandingGroup;
    if (!group) {
      return null;
    }

    const raw = group.getRawValue();
    const settings: HospitalityLandingSettings = {};

    const welcome = this.cleanHospitalityString(raw.welcomeMessage);
    if (welcome) {
      settings.welcomeMessage = welcome;
    }

    const notes = this.cleanHospitalityString(raw.guestNotes);
    if (notes) {
      settings.guestNotes = notes;
    }

    const hostName = this.cleanHospitalityString(raw.hostContact?.name);
    const hostPhone = this.cleanHospitalityString(raw.hostContact?.phone);
    const hostEmail = this.cleanHospitalityString(raw.hostContact?.email);
    const hostWhatsapp = this.cleanHospitalityString(raw.hostContact?.whatsapp);
    if (hostName || hostPhone || hostEmail || hostWhatsapp) {
      settings.hostContact = {};
      if (hostName) { settings.hostContact.name = hostName; }
      if (hostPhone) { settings.hostContact.phone = hostPhone; }
      if (hostEmail) { settings.hostContact.email = hostEmail; }
      if (hostWhatsapp) { settings.hostContact.whatsapp = hostWhatsapp; }
    }

    const supportHours = this.cleanHospitalityString(raw.hostContact?.supportHours);
    if (supportHours) {
      settings.hostContact = settings.hostContact || {};
      settings.hostContact.supportHours = supportHours;
    }

    if (raw.feedback) {
      const instructions = this.cleanHospitalityString(raw.feedback.instructions);
      const email = this.cleanHospitalityString(raw.feedback.email);
      const phone = this.cleanHospitalityString(raw.feedback.phone);
      const formUrl = this.cleanHospitalityString(raw.feedback.formUrl);
      if (instructions || email || phone || formUrl) {
        settings.feedback = {};
        if (instructions) { settings.feedback.instructions = instructions; }
        if (email) { settings.feedback.email = email; }
        if (phone) { settings.feedback.phone = phone; }
        if (formUrl) { settings.feedback.formUrl = formUrl; }
      }
    }

    return Object.keys(settings).length ? settings : null;
  }

  protected extractHospitalityEntries(): HospitalityCatalogEntries | null {
    const group = this.hospitalityEntriesGroup;
    if (!group) {
      return null;
    }

    const raw = group.getRawValue() as HospitalityCatalogEntries & {
      applianceGuides?: Array<HospitalityCatalogApplianceGuide & { steps?: string[]; isEditing?: boolean }>;
    };

    const clean = (value?: string | null) => this.cleanHospitalityString(value);
    const mapLinks = (links?: HospitalityCatalogLink[]): HospitalityCatalogLink[] =>
      Array.isArray(links)
        ? links
            .map((link) => this.cleanHospitalityLink(link))
            .filter((link): link is HospitalityCatalogLink => !!link)
        : [];

    const mapDigitalKeys = (
      keys?: Array<HospitalityDigitalKey | HospitalityCatalogLink>,
    ): HospitalityDigitalKey[] =>
      Array.isArray(keys)
        ? keys
            .map((key) => this.cleanHospitalityDigitalKey(key))
            .filter((key): key is HospitalityDigitalKey => !!key)
        : [];

    const result: HospitalityCatalogEntries = {};
    let hasPayload = false;

    const normalizeEntry = <T extends { [key: string]: any }>(
      entry: T,
      fields: Array<{ key: keyof T; required?: boolean }>,
      transform?: (data: T, output: any) => void
    ): any | null => {
      if (!entry) {
        return null;
      }

      const output: any = {};
      if ((entry as any)._id) {
        output._id = (entry as any)._id;
      }
      if (typeof (entry as any).order === 'number') {
        output.order = (entry as any).order;
      }

      const isDeleted = (entry as any).isDeleted === true;
      output.isDeleted = isDeleted;
      if (isDeleted) {
        output.deletedAt = (entry as any).deletedAt ? new Date((entry as any).deletedAt).toISOString() : new Date().toISOString();
        hasPayload = true;
        if (transform) {
          transform(entry, output);
        }
        return output;
      }

      fields.forEach(({ key }) => {
        if (key === 'links') {
          const links = mapLinks((entry as any).links);
          if (links.length) {
            output.links = links;
          }
          return;
        }
        const value = clean((entry as any)[key]);
        if (value) {
          output[key as string] = value;
        }
      });

      if (transform) {
        transform(entry, output);
      }

      const missingRequired = fields.some(({ key, required }) => {
        if (!required) {
          return false;
        }
        const value = output[key as string];
        return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
      });

      if (missingRequired) {
        return null;
      }

      if (Object.keys(output).length > 0) {
        hasPayload = true;
        return output;
      }

      return null;
    };

    const houseRules = Array.isArray(raw.houseRules)
      ? raw.houseRules
          .map((entry) =>
            normalizeEntry(entry as any, [
              { key: 'rule', required: true },
              { key: 'links' },
            ])
          )
          .filter((entry): entry is HospitalityCatalogHouseRule => !!entry)
      : [];
    if (houseRules.length || Array.isArray(raw.houseRules)) {
      result.houseRules = houseRules;
    }

    const checkIns = Array.isArray(raw.checkIns)
      ? raw.checkIns
          .map((entry) =>
            normalizeEntry(
              entry as any,
              [
                { key: 'title', required: true },
                { key: 'instructions' },
                { key: 'location' },
                { key: 'contact' },
                { key: 'links' },
              ],
              (source, output) => {
                const schedule = (source as any).schedule || {};
                const from = clean(schedule.from);
                const until = clean(schedule.until);
                if (from || until) {
                  output.schedule = { from, until };
                }
                const digitalKeys = mapDigitalKeys((source as any).digitalKeys);
                if (digitalKeys.length) {
                  output.digitalKeys = digitalKeys;
                }
              }
            )
          )
          .filter((entry): entry is HospitalityCatalogCheckInEntry => !!entry)
      : [];
    if (checkIns.length || Array.isArray(raw.checkIns)) {
      result.checkIns = checkIns;
    }

    const checkOuts = Array.isArray(raw.checkOuts)
      ? raw.checkOuts
          .map((entry) =>
            normalizeEntry(
              entry as any,
              [
                { key: 'title', required: true },
                { key: 'instructions' },
                { key: 'dropOffLocation' },
                { key: 'contact' },
                { key: 'links' },
              ],
              (source, output) => {
                const checkOutTime = clean((source as any).checkOutTime) || clean((source as any).deadline);
                if (checkOutTime) {
                  output.checkOutTime = checkOutTime;
                }
                const checklist = (source as any).checklist;
                if (checklist) {
                  const title = clean(checklist.title);
                  const items = Array.isArray(checklist.items)
                    ? (checklist.items as Array<string | null | undefined>)
                        .map((item) => this.cleanHospitalityString(item) || '')
                        .map((item) => item.trim())
                        .filter(Boolean)
                    : [];
                  if (title || items.length) {
                    output.checklist = {
                      title: title || undefined,
                      items: items.length ? items : undefined,
                    };
                  }
                }
              }
            )
          )
          .filter((entry): entry is HospitalityCatalogCheckOutEntry => !!entry)
      : [];
    if (checkOuts.length || Array.isArray(raw.checkOuts)) {
      result.checkOuts = checkOuts;
    }

    const applianceGuides = Array.isArray(raw.applianceGuides)
      ? raw.applianceGuides
          .map((entry) =>
            normalizeEntry(
              entry as any,
              [
                { key: 'appliance', required: true },
                { key: 'summary', required: true },
                { key: 'links' },
              ],
              (source, output) => {
                const steps = this.normalizeApplianceGuideSteps(
                  Array.isArray((source as any).steps) ? ((source as any).steps as Array<string | null | undefined>) : null,
                  (source as any).details,
                );
                output.steps = steps;
              }
            )
          )
          .filter((entry): entry is HospitalityCatalogApplianceGuide => !!entry)
      : [];
    if (applianceGuides.length || Array.isArray(raw.applianceGuides)) {
      result.applianceGuides = applianceGuides;
    }

    const additionalInfos = Array.isArray(raw.additionalInfos)
      ? raw.additionalInfos
          .map((entry) =>
            normalizeEntry(entry as any, [
              { key: 'title', required: true },
              { key: 'body', required: true },
              { key: 'links' },
            ], (source, output) => {
              if (output.body) {
                return;
              }
              const fallback = clean((source as any)?.details);
              if (fallback) {
                output.body = fallback;
              }
            })
          )
          .filter((entry): entry is HospitalityCatalogAdditionalInfo => !!entry)
      : [];
    if (additionalInfos.length || Array.isArray(raw.additionalInfos)) {
      result.additionalInfos = additionalInfos;
    }

    return hasPayload ? result : null;
  }

  protected get hospitalityLandingGroup(): FormGroup | null {
    return (this.companyForm?.get('hospitalityLanding') as FormGroup) || null;
  }

  protected get hospitalityEntriesGroup(): FormGroup | null {
    return (this.companyForm?.get('hospitalityEntries') as FormGroup) || null;
  }

  protected setHospitalityLanding(settings?: HospitalityLandingSettings | null): void {
    const group = this.companyForm.get('hospitalityLanding') as FormGroup | null;
    if (!group) {
      return;
    }

    group.patchValue(
      {
        welcomeMessage: settings?.welcomeMessage || '',
        guestNotes: settings?.guestNotes || '',
        hostContact: {
          name: settings?.hostContact?.name || '',
          phone: settings?.hostContact?.phone || '',
          email: settings?.hostContact?.email || '',
          whatsapp: settings?.hostContact?.whatsapp || '',
          supportHours: settings?.hostContact?.supportHours || '',
        },
        feedback: {
          instructions: settings?.feedback?.instructions || '',
          email: settings?.feedback?.email || '',
          phone: settings?.feedback?.phone || '',
          formUrl: settings?.feedback?.formUrl || '',
        },
      },
      { emitEvent: false }
    );

    const hostContactGroup = group.get('hostContact') as FormGroup | null;
    if (hostContactGroup) {
      this.setHostSupportHourSlots(hostContactGroup, settings?.hostContact?.supportHours || '');
    }
  }

  protected setHospitalityEntries(entries?: HospitalityCatalogEntries | null): void {
    const group = this.companyForm.get('hospitalityEntries') as FormGroup | null;
    if (!group) {
      return;
    }

    const setArray = (
      key: keyof HospitalityCatalogEntries,
      values: any[] | undefined,
      factory: (value?: any) => FormGroup
    ): void => {
      const array = group.get(key as string) as FormArray | null;
      if (!array) {
        return;
      }
      array.clear({ emitEvent: false });
      (values || []).forEach((value) => array.push(factory(value)));
    };

    setArray('houseRules', entries?.houseRules, (value) => this.createHouseRuleEntryGroup(value));
    setArray('checkIns', entries?.checkIns, (value) => this.createCheckInEntryGroup(value));
    setArray('checkOuts', entries?.checkOuts, (value) => this.createCheckOutEntryGroup(value));
    setArray('applianceGuides', entries?.applianceGuides, (value) => this.createApplianceGuideEntryGroup(value));
    setArray('additionalInfos', entries?.additionalInfos, (value) => this.createAdditionalInfoEntryGroup(value));
  }

  protected optionalUrlValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = this.cleanHospitalityString(control.value);
    if (!value) {
      return null;
    }
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-zA-Z]{2,})([\/\w .-]*)*\/?$/;
    if (!urlPattern.test(value)) {
      return { invalidUrl: true };
    }
    return null;
  }

  protected hospitalitySectionDuplicateValidator(
    sectionKey: keyof HospitalityCatalogEntries,
    field: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }
      const array = control.get(sectionKey as string) as FormArray | null;
      if (!array) {
        return null;
      }

      const seen = new Set<string>();
      for (let i = 0; i < array.length; i++) {
        const group = array.at(i) as FormGroup;
        const value = this.cleanHospitalityString(group.get(field)?.value);
        if (!value) {
          continue;
        }
        const key = value.toLowerCase();
        if (seen.has(key)) {
          return { [`duplicate_${String(sectionKey)}`]: { field, index: i } };
        }
        seen.add(key);
      }
      return null;
    };
  }

  protected getHospitalityCatalogArray(sectionKey: keyof HospitalityCatalogEntries): FormArray | null {
    return (this.hospitalityEntriesGroup?.get(sectionKey as string) as FormArray) || null;
  }

  getVisibleHospitalityControls(sectionKey: keyof HospitalityCatalogEntries): HospitalityEntryTrackable[] {
    const array = this.getHospitalityCatalogArray(sectionKey);
    if (!array) {
      return [];
    }
    return array.controls
      .map((control, index) => {
        const group = control as FormGroup;
        const rawId = group.get('_id')?.value;
        return {
          control: group,
          index,
          trackId: rawId ?? `temp-${index}`,
        };
      })
      .filter((entry) => !entry.control.get('isDeleted')?.value);
  }

  private parseTimeStringToDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private normalizeTimeValue(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      return this.formatTime(value);
    }
    if (typeof value === 'string') {
      const cleaned = this.cleanHospitalityString(value);
      if (!cleaned) {
        return undefined;
      }
      const parsed = this.parseTimeStringToDate(cleaned);
      return parsed ? this.formatTime(parsed) : cleaned;
    }
    return undefined;
  }

  private formatSupportHourSlot(slot: TimeRangeSlot): string | undefined {
    const start = this.normalizeTimeValue(slot.start);
    const end = this.normalizeTimeValue(slot.end);
    if (start && end) {
      return `${start} - ${end}`;
    }
    if (start || end) {
      return start || end;
    }
    return undefined;
  }

  private parseSupportHourSlots(value?: string | null): TimeRangeSlot[] {
    if (!value) {
      return [];
    }
    return value
      .split(/[,;]+/)
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0)
      .map(segment => {
        const rangeMatch = segment.match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
        if (rangeMatch) {
          return {
            start: this.parseTimeStringToDate(rangeMatch[1]),
            end: this.parseTimeStringToDate(rangeMatch[2]),
          } as TimeRangeSlot;
        }
        const singleMatch = segment.match(/(\d{1,2}:\d{2})/);
        if (singleMatch) {
          return { start: this.parseTimeStringToDate(singleMatch[1]), end: null } as TimeRangeSlot;
        }
        return { start: null, end: null } as TimeRangeSlot;
      });
  }

  private createSupportHourSlotGroup(slot?: TimeRangeSlot): FormGroup {
    return this.fb.group({
      start: [slot?.start || null],
      end: [slot?.end || null],
    });
  }

  private setupHostSupportHourSlotSync(group: FormGroup): void {
    const array = group.get('supportHoursSlots') as FormArray | null;
    if (!array) {
      return;
    }
    array.valueChanges.subscribe(() => this.syncHostSupportHourSlots(group));
  }

  private setHostSupportHourSlots(group: FormGroup, value?: string | null): void {
    const array = group.get('supportHoursSlots') as FormArray | null;
    if (!array) {
      return;
    }
    array.clear({ emitEvent: false });
    const slots = this.parseSupportHourSlots(value);
    if (slots.length) {
      slots.forEach(slot => array.push(this.createSupportHourSlotGroup(slot)));
      group.get('supportHours')?.setValue(slots.map(slot => this.formatSupportHourSlot(slot)).filter(Boolean).join(', '), {
        emitEvent: false,
      });
    } else {
      array.push(this.createSupportHourSlotGroup());
      group.get('supportHours')?.setValue('', { emitEvent: false });
    }
  }

  private syncHostSupportHourSlots(group: FormGroup): void {
    const array = group.get('supportHoursSlots') as FormArray | null;
    if (!array) {
      return;
    }
    const formatted = array.controls
      .map(slotControl => this.formatSupportHourSlot(slotControl.value as TimeRangeSlot))
      .filter((slot): slot is string => !!slot);
    group.get('supportHours')?.setValue(formatted.join(', '), { emitEvent: false });
  }

  getHostSupportHourSlotsArray(): FormArray | null {
    return (this.hospitalityLandingGroup?.get('hostContact') as FormGroup | null)?.get('supportHoursSlots') as FormArray | null;
  }

  hostSupportHourSlotsLength(): number {
    return this.getHostSupportHourSlotsArray()?.length || 0;
  }

  addHostSupportHourSlot(): void {
    const array = this.getHostSupportHourSlotsArray();
    if (!array) {
      return;
    }
    array.push(this.createSupportHourSlotGroup());
  }

  getHostSupportHourSlotControls(): AbstractControl[] {
    return this.getHostSupportHourSlotsArray()?.controls || [];
  }

  removeHostSupportHourSlot(index: number): void {
    const array = this.getHostSupportHourSlotsArray();
    if (!array) {
      return;
    }
    array.removeAt(index);
    this.syncHostSupportHourSlots(this.hospitalityLandingGroup?.get('hostContact') as FormGroup);
  }

  onHostSupportHourStartChange(index: number): void {
    const array = this.getHostSupportHourSlotsArray();
    if (!array) {
      return;
    }
    const slotControl = array.at(index) as FormGroup | null;
    if (!slotControl) {
      return;
    }
    const start = slotControl.get('start')?.value as Date | null;
    const endControl = slotControl.get('end');
    if (!start || !endControl) {
      return;
    }
    const end = endControl.value as Date | null;
    if (!end || (end && start && end < start)) {
      endControl.setValue(start, { emitEvent: false });
    }
  }

  getDisabledHostSupportHourEndHours(index: number): () => number[] {
    return () => {
      const array = this.getHostSupportHourSlotsArray();
      const slot = array?.at(index) as FormGroup | null;
      const start = slot?.get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      const hour = start.getHours();
      return Array.from({ length: hour }, (_, i) => i);
    };
  }

  getDisabledHostSupportHourEndMinutes(index: number): (hour: number) => number[] {
    return (hour: number) => {
      const array = this.getHostSupportHourSlotsArray();
      const slot = array?.at(index) as FormGroup | null;
      const start = slot?.get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      if (hour !== start.getHours()) {
        return [];
      }
      const minute = start.getMinutes();
      return Array.from({ length: minute }, (_, i) => i);
    };
  }

  getDisabledHostSupportHourEndSeconds(index: number): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const array = this.getHostSupportHourSlotsArray();
      const slot = array?.at(index) as FormGroup | null;
      const start = slot?.get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      if (hour !== start.getHours() || minute !== start.getMinutes()) {
        return [];
      }
      const second = start.getSeconds();
      return Array.from({ length: second }, (_, i) => i);
    };
  }

  getCheckInEntryScheduleGroup(index: number): FormGroup | null {
    const array = this.getHospitalityCatalogArray('checkIns');
    if (!array) {
      return null;
    }
    return (array.at(index)?.get('schedule') as FormGroup) || null;
  }

  onCheckInEntryStartChange(index: number): void {
    const schedule = this.getCheckInEntryScheduleGroup(index);
    if (!schedule) {
      return;
    }
    const from = schedule.get('from')?.value as Date | null;
    const untilControl = schedule.get('until') as FormControl | null;
    const until = untilControl?.value as Date | null;
    if (from && until && until < from) {
      untilControl?.setValue(null);
    }
  }

  getDisabledCheckInEntryUntilHours(index: number): () => number[] {
    return () => {
      const schedule = this.getCheckInEntryScheduleGroup(index);
      const from = schedule?.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      const hour = from.getHours();
      return Array.from({ length: hour }, (_, i) => i);
    };
  }

  getDisabledCheckInEntryUntilMinutes(index: number): (hour: number) => number[] {
    return (hour: number) => {
      const schedule = this.getCheckInEntryScheduleGroup(index);
      const from = schedule?.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      if (hour !== from.getHours()) {
        return [];
      }
      const minute = from.getMinutes();
      return Array.from({ length: minute }, (_, i) => i);
    };
  }

  getDisabledCheckInEntryUntilSeconds(index: number): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const schedule = this.getCheckInEntryScheduleGroup(index);
      const from = schedule?.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      if (hour !== from.getHours() || minute !== from.getMinutes()) {
        return [];
      }
      const second = from.getSeconds();
      return Array.from({ length: second }, (_, i) => i);
    };
  }

  private createHospitalityLandingGroup(settings?: HospitalityLandingSettings): FormGroup {
    const group = this.fb.group({
      welcomeMessage: [
        settings?.welcomeMessage || '',
        [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.welcomeMessage)],
      ],
      guestNotes: [
        settings?.guestNotes || '',
        [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.guestNotes)],
      ],
      hostContact: this.fb.group({
        name: [
          settings?.hostContact?.name || '',
          [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.hostName)],
        ],
        phone: [
          settings?.hostContact?.phone || '',
          [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.hostPhone), phoneValidator()],
        ],
        email: [
          settings?.hostContact?.email || '',
          [Validators.email, Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.hostEmail)],
        ],
        whatsapp: [
          settings?.hostContact?.whatsapp || '',
          [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.hostWhatsapp)],
        ],
        supportHours: [''],
        supportHoursSlots: this.fb.array([]),
      }),
      feedback: this.fb.group({
        instructions: [
          settings?.feedback?.instructions || '',
          [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.feedbackInstructions)],
        ],
        email: [
          settings?.feedback?.email || '',
          [Validators.email, Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.feedbackEmail)],
        ],
        phone: [
          settings?.feedback?.phone || '',
          [Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.feedbackPhone), phoneValidator()],
        ],
        formUrl: [
          settings?.feedback?.formUrl || '',
          [
            Validators.maxLength(this.HOSPITALITY_LANDING_LIMITS.feedbackFormUrl),
            (control: AbstractControl) => this.optionalUrlValidator(control),
          ],
        ],
      }),
    });
    const hostContactGroup = group.get('hostContact') as FormGroup;
    this.setupHostSupportHourSlotSync(hostContactGroup);
    this.setHostSupportHourSlots(hostContactGroup, settings?.hostContact?.supportHours || '');
    return group;
  }

  private createHospitalityEntriesGroup(entries?: HospitalityCatalogEntries): FormGroup {
    const group = this.fb.group({
      houseRules: this.fb.array((entries?.houseRules || []).map((entry) => this.createHouseRuleEntryGroup(entry))),
      checkIns: this.fb.array((entries?.checkIns || []).map((entry) => this.createCheckInEntryGroup(entry))),
      checkOuts: this.fb.array((entries?.checkOuts || []).map((entry) => this.createCheckOutEntryGroup(entry))),
      applianceGuides: this.fb.array((entries?.applianceGuides || []).map((entry) => this.createApplianceGuideEntryGroup(entry))),
      additionalInfos: this.fb.array((entries?.additionalInfos || []).map((entry) => this.createAdditionalInfoEntryGroup(entry))),
    });

    group.setValidators([
      this.hospitalitySectionDuplicateValidator('houseRules', 'rule'),
      this.hospitalitySectionDuplicateValidator('checkIns', 'title'),
      this.hospitalitySectionDuplicateValidator('checkOuts', 'title'),
      this.hospitalitySectionDuplicateValidator('applianceGuides', 'appliance'),
      this.hospitalitySectionDuplicateValidator('additionalInfos', 'title'),
    ]);

    return group;
  }

  private createHospitalityEntryLinkGroup(link?: HospitalityCatalogLink): FormGroup {
    return this.fb.group({
      label: [link?.label || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.linkLabel)]],
      url: [link?.url || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.linkUrl), (control: AbstractControl) => this.optionalUrlValidator(control)]],
      document: this.fb.control(link?.document ? { ...link.document } : null),
    });
  }

  private createHospitalityDigitalKeyGroup(
    key?: HospitalityDigitalKey | HospitalityCatalogLink | null,
  ): FormGroup {
    const normalized = this.normalizeHospitalityDigitalKeyInput(key);
    const group = this.fb.group({
      accessType: [normalized?.accessType || 'pin', [Validators.required]],
      code: [normalized?.code || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyCode)]],
      link: [
        normalized?.link || '',
        [
          Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyLink),
          (control: AbstractControl) => this.optionalUrlValidator(control),
        ],
      ],
      notes: [normalized?.notes || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyNotes)]],
    });
    group.setValidators(this.digitalKeyGroupValidator());
    return group;
  }

  private createHouseRuleEntryGroup(entry?: HospitalityCatalogHouseRule): FormGroup {
    const resolvedRule = this.cleanHospitalityString(entry?.rule)
      || this.cleanHospitalityString((entry as any)?.title)
      || this.cleanHospitalityString((entry as any)?.summary)
      || this.cleanHospitalityString((entry as any)?.details)
      || '';
    return this.fb.group({
      _id: [entry?._id || null],
      rule: [resolvedRule, [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.rule)]],
      links: this.fb.array((entry?.links || []).map((link) => this.createHospitalityEntryLinkGroup(link))),
      order: [typeof entry?.order === 'number' ? entry?.order : null],
      isDeleted: [entry?.isDeleted || false],
      deletedAt: [entry?.deletedAt || null],
      isEditing: [false],
    });
  }

  private createCheckInEntryGroup(entry?: HospitalityCatalogCheckInEntry): FormGroup {
    const schedule = entry?.schedule || {};
    const legacySummary = typeof (entry as any)?.summary === 'string' ? (entry as any).summary.trim() : '';
    const legacyDetails = typeof (entry as any)?.details === 'string' ? (entry as any).details : '';
    const resolvedInstructions =
      typeof entry?.instructions === 'string' && entry.instructions.length
        ? entry.instructions
        : legacySummary || legacyDetails || '';
    return this.fb.group({
      _id: [entry?._id || null],
      title: [entry?.title || '', [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      instructions: [resolvedInstructions, [Validators.maxLength(this.HOSPITALITY_LIMITS.instructions)]],
      schedule: this.fb.group({
        from: [this.parseTimeStringToDate(schedule?.from)],
        until: [this.parseTimeStringToDate(schedule?.until)],
      }),
      location: [
        entry?.location || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.location), mapAddressValidator()],
      ],
      contact: [entry?.contact || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.contact)]],
      links: this.fb.array((entry?.links || []).map((link) => this.createHospitalityEntryLinkGroup(link))),
      digitalKeys: this.fb.array((entry?.digitalKeys || []).map((key) => this.createHospitalityDigitalKeyGroup(key))),
      order: [typeof entry?.order === 'number' ? entry?.order : null],
      isDeleted: [entry?.isDeleted || false],
      deletedAt: [entry?.deletedAt || null],
      isEditing: [false],
    });
  }

  private createCheckOutEntryGroup(entry?: HospitalityCatalogCheckOutEntry): FormGroup {
    const checklist = entry?.checklist;
    const legacySummary = this.cleanHospitalityString((entry as any)?.summary) || '';
    const legacyDetails = this.cleanHospitalityString((entry as any)?.details) || '';
    const legacyDeadline = this.cleanHospitalityString((entry as any)?.deadline) || '';
    const legacyLocation = this.cleanHospitalityString((entry as any)?.location) || '';
    return this.fb.group({
      _id: [entry?._id || null],
      title: [entry?.title || '', [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      instructions: [entry?.instructions || legacySummary || legacyDetails || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.instructions)]],
      checkOutTime: [this.parseTimeStringToDate(entry?.checkOutTime || legacyDeadline || undefined)],
      dropOffLocation: [
        entry?.dropOffLocation || legacyLocation || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.location), mapAddressValidator()],
      ],
      contact: [entry?.contact || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.contact)]],
      links: this.fb.array((entry?.links || []).map((link) => this.createHospitalityEntryLinkGroup(link))),
      checklist: this.fb.group({
        title: [
          checklist?.title || '',
          [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistTitle)],
        ],
        items: this.fb.array(
          (checklist?.items || []).map((item) =>
            this.fb.control(item || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistItem)])
          ),
        ),
      }),
      order: [typeof entry?.order === 'number' ? entry?.order : null],
      isDeleted: [entry?.isDeleted || false],
      deletedAt: [entry?.deletedAt || null],
      isEditing: [false],
    });
  }

  private normalizeApplianceGuideSteps(
    stepsInput?: Array<string | null | undefined> | null,
    detailsInput?: string | null | undefined,
  ): string[] {
    const sanitizedSteps = Array.isArray(stepsInput)
      ? stepsInput
          .map((step) => this.cleanHospitalityString(step) || '')
          .map((step) => step.trim())
          .filter(Boolean)
      : [];
    if (sanitizedSteps.length) {
      return sanitizedSteps;
    }
    const legacyDetails = this.cleanHospitalityString(detailsInput) || '';
    if (!legacyDetails) {
      return [];
    }
    return legacyDetails
      .split('\n')
      .map((step) => step.trim())
      .filter(Boolean);
  }

  private createApplianceGuideEntryGroup(entry?: HospitalityCatalogApplianceGuide): FormGroup {
    const steps = Array.isArray(entry?.steps)
      ? entry?.steps.map((step) => this.cleanHospitalityString(step) || '').filter(Boolean)
      : [];
    const legacyDetails = this.cleanHospitalityString((entry as any)?.details) || '';
    const resolvedSteps = steps.length ? steps : legacyDetails
      .split('\n')
      .map((step) => step.trim())
      .filter(Boolean);

    return this.fb.group({
      _id: [entry?._id || null],
      appliance: [entry?.appliance || '', [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.appliance)]],
      summary: [entry?.summary || '', [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.summary)]],
      steps: this.fb.array(
        resolvedSteps.length
          ? resolvedSteps.map((step) => this.fb.control(step, [Validators.maxLength(this.HOSPITALITY_LIMITS.details)]))
          : [this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.details)])],
      ),
      links: this.fb.array((entry?.links || []).map((link) => this.createHospitalityEntryLinkGroup(link))),
      order: [typeof entry?.order === 'number' ? entry?.order : null],
      isDeleted: [entry?.isDeleted || false],
      deletedAt: [entry?.deletedAt || null],
      isEditing: [false],
    });
  }

  private createAdditionalInfoEntryGroup(entry?: HospitalityCatalogAdditionalInfo): FormGroup {
    const resolvedBody =
      this.cleanHospitalityString(entry?.body)
        || this.cleanHospitalityString((entry as any)?.details)
        || '';
    return this.fb.group({
      _id: [entry?._id || null],
      title: [entry?.title || '', [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      body: [resolvedBody, [Validators.required, Validators.maxLength(this.HOSPITALITY_LIMITS.details)]],
      links: this.fb.array((entry?.links || []).map((link) => this.createHospitalityEntryLinkGroup(link))),
      order: [typeof entry?.order === 'number' ? entry?.order : null],
      isDeleted: [entry?.isDeleted || false],
      deletedAt: [entry?.deletedAt || null],
      isEditing: [false],
    });
  }

  private collapseOtherHospitalitySections(activeKey: keyof HospitalityCatalogEntries): void {
    this.HOSPITALITY_SECTION_KEYS.forEach((key) => {
      if (key !== activeKey) {
        this.hospitalitySectionExpanded[key] = false;
      }
    });
  }

  private digitalKeyGroupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }
      const accessType = this.normalizeHospitalityDigitalKeyAccessType(control.get('accessType')?.value);
      const code = this.cleanHospitalityString(control.get('code')?.value);
      const link = this.cleanHospitalityString(control.get('link')?.value);
      const notes = this.cleanHospitalityString(control.get('notes')?.value);

      if (!accessType) {
        return { digitalKeyAccessRequired: true };
      }
      if (accessType === 'pin') {
        return code ? null : { digitalKeyValueRequired: 'code' };
      }
      if (accessType === 'app') {
        return link ? null : { digitalKeyValueRequired: 'link' };
      }
      if (accessType === 'other') {
        return notes ? null : { digitalKeyValueRequired: 'notes' };
      }
      return { digitalKeyAccessRequired: true };
    };
  }

  private normalizeHospitalityDigitalKeyAccessType(
    value: unknown,
  ): HospitalityDigitalKeyAccessType | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalized = value.trim().toLowerCase();
    return this.digitalKeyAccessTypes.find(type => type === normalized) as HospitalityDigitalKeyAccessType | undefined;
  }

  private normalizeHospitalityDigitalKeyInput(
    key?: HospitalityDigitalKey | HospitalityCatalogLink | null,
  ): HospitalityDigitalKey | undefined {
    if (!key || typeof key !== 'object') {
      return undefined;
    }

    if ('accessType' in key) {
      const candidate = key as HospitalityDigitalKey;
      const accessType = this.normalizeHospitalityDigitalKeyAccessType(candidate.accessType);
      if (!accessType) {
        return undefined;
      }
      const normalized: HospitalityDigitalKey = { accessType };
      const code = this.cleanHospitalityString(candidate.code);
      const link = this.cleanHospitalityString(candidate.link);
      const notes = this.cleanHospitalityString(candidate.notes);
      if (code) { normalized.code = code; }
      if (link) { normalized.link = link; }
      if (notes) { normalized.notes = notes; }
      return normalized;
    }

    const linkCandidate = key as HospitalityCatalogLink;
    const label = this.cleanHospitalityString(linkCandidate.label);
    const url = this.cleanHospitalityString(linkCandidate.url);

    if (url) {
      const normalized: HospitalityDigitalKey = { accessType: 'app', link: url };
      if (label) {
        normalized.notes = label;
      }
      return normalized;
    }

    if (label) {
      return { accessType: 'other', notes: label };
    }

    return undefined;
  }

  private cleanHospitalityDigitalKey(
    key?: HospitalityDigitalKey | HospitalityCatalogLink | null,
  ): HospitalityDigitalKey | undefined {
    const normalized = this.normalizeHospitalityDigitalKeyInput(key);
    if (!normalized) {
      return undefined;
    }

    if (normalized.accessType === 'pin') {
      return normalized.code
        ? { accessType: 'pin', code: normalized.code }
        : undefined;
    }

    if (normalized.accessType === 'app') {
      if (!normalized.link) {
        return undefined;
      }
      const cleaned: HospitalityDigitalKey = { accessType: 'app', link: normalized.link };
      if (normalized.notes) {
        cleaned.notes = normalized.notes;
      }
      return cleaned;
    }

    if (!normalized.notes) {
      return undefined;
    }
    const cleaned: HospitalityDigitalKey = { accessType: 'other', notes: normalized.notes };
    if (normalized.link) {
      cleaned.link = normalized.link;
    }
    return cleaned;
  }

  private cleanHospitalityString(value?: string | Date | null): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      return formatDate(value, 'HH:mm', this.translate.currentLang || 'en-US');
    }
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : undefined;
  }

  private cleanHospitalityLink(
    link?: HospitalityLandingLink | HospitalityCatalogLink | null,
  ): HospitalityCatalogLink | undefined {
    if (!link) {
      return undefined;
    }
    const cleaned: HospitalityCatalogLink = {};
    const label = this.cleanHospitalityString(link.label);
    const url = this.cleanHospitalityString(link.url);
    if (label) {
      cleaned.label = label;
    }
    if (url) {
      cleaned.url = url;
    }
    const document = this.cleanHospitalityLinkDocument((link as HospitalityCatalogLink)?.document);
    if (document) {
      cleaned.document = document;
    }
    return Object.keys(cleaned).length ? cleaned : undefined;
  }

  private cleanHospitalityLinkDocument(document?: HospitalityLinkDocument | null): HospitalityLinkDocument | undefined {
    if (!document) {
      return undefined;
    }
    const cleaned: HospitalityLinkDocument = {};
    const fileName = this.cleanHospitalityString(document.fileName);
    if (fileName) {
      cleaned.fileName = fileName;
    }
    const fileType = this.cleanHospitalityString(document.fileType);
    if (fileType) {
      cleaned.fileType = fileType;
    }
    const url = this.cleanHospitalityString(document.url);
    if (url) {
      cleaned.url = url;
    }
    const s3Key = this.cleanHospitalityString(document.s3Key);
    if (s3Key) {
      cleaned.s3Key = s3Key;
    }
    const uploadedAt = document.uploadedAt;
    if (uploadedAt) {
      const resolved = uploadedAt instanceof Date ? uploadedAt : new Date(uploadedAt);
      if (!Number.isNaN(resolved.getTime())) {
        cleaned.uploadedAt = resolved.toISOString();
      }
    }
    return Object.keys(cleaned).length ? cleaned : undefined;
  }
  public get hasHospitalityContent(): boolean {
    if (this.appMode !== 'hospitality') {
      return false;
    }
    const p = this.hospitalityPreview || { infoSections: [] };

    const hasHero = !!(p.welcomeMessage || p.guestNotes);
    const hasHost = !!p.hostContact; // already only set when it has details
    const hasTimeline = Array.isArray(p.timeline) && p.timeline.length > 0;
    const hasRules = !!(p.houseRules && Array.isArray(p.houseRules.rules) && p.houseRules.rules.length > 0);
    const hasGuides = Array.isArray(p.guides) && p.guides.length > 0;
    const hasInfo = Array.isArray(p.infoSections) && p.infoSections.length > 0;
    const hasFeedback = !!p.feedback; // only set when non-empty in computeHospitalityPreview()

    return hasHero || hasHost || hasTimeline || hasRules || hasGuides || hasInfo || hasFeedback;
  }
}
